import React, {useCallback} from 'react';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import FlashMessage from '@/components/flash-message';
import useAcceptProposal from '@/hooks/mutations/use-accept-proposal';

/**
 * Shared accept-with-flash behavior used by both the row overflow menu and the
 * grid card CTA, so the two "Accept" entry points can't drift on copy or error
 * handling. Callers are responsible for gating visibility/disabled state
 * (paidGigId presence, pending negotiation, etc.).
 */
function useAcceptCreator(onActionComplete: () => void) {
  const acceptProposal = useAcceptProposal();
  // React Query guarantees `mutate` a stable identity, so memoizing on it
  // keeps acceptCreator stable across renders (consumers put it in
  // useCallback deps — e.g. the virtualized grid's renderItem).
  const {mutate: mutateAcceptProposal} = acceptProposal;

  const acceptCreator = useCallback(
    (creator: CampaignCreatorRow) => {
      if (!creator.paidGigId) return;

      mutateAcceptProposal(
        {paidGigId: creator.paidGigId},
        {
          onSuccess: () => {
            raiseFlashMessage({
              status: 'success',
              mode: 'light',
              message: (
                <FlashMessage.Banner
                  title={translate('Creator accepted')}
                  subtitle={creator.creatorName}
                />
              ),
            });
            onActionComplete();
          },
          onError: (error: unknown) => {
            raiseFlashMessageError({
              title: translate('Failed to accept creator'),
              subtitle: extractBackendErrorDetail(error),
            });
            onActionComplete();
          },
        }
      );
    },
    [mutateAcceptProposal, onActionComplete]
  );

  return {acceptCreator, isAccepting: acceptProposal.isLoading};
}

export default useAcceptCreator;
